import { useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  addMonths,
  currentMonthSP,
  daysInMonth,
  effDay,
  formatMonthLabel,
  formatYmd,
  monthName,
  monthOf,
  parseYm,
  parseYmd,
  prevDay,
  todaySP,
  type Ym,
  type Ymd,
} from '@lira/shared';
import { cn } from '@/lib/cn';
import { formatDatePretty, type TxAccent } from './txMeta';

const EASE = [0.16, 1, 0.3, 1] as const;
const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'] as const;

const ACTIVE_CHIP: Record<TxAccent, string> = {
  expense: 'border-expense bg-expense-weak text-expense',
  income: 'border-income bg-income-weak text-income',
  balance: 'border-balance bg-balance/10 text-balance',
  cards: 'border-cards bg-cards/10 text-cards',
};

/** Dia da semana (0=domingo) sem parse de string date-only (usa UTC). */
function weekdayOfFirst(year: number, month: number): number {
  return new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
}

interface MiniCalendarProps {
  value: Ymd;
  initialMonth: Ym;
  onSelect: (d: Ymd) => void;
}

/** Calendário de 1 mês; abre no mês pedido (não no de hoje). Semana começa domingo. */
function MiniCalendar({ value, initialMonth, onSelect }: MiniCalendarProps) {
  const [viewYm, setViewYm] = useState<Ym>(monthOf(value) || initialMonth);
  const { year, month } = parseYm(viewYm);
  const total = daysInMonth(year, month);
  const offset = weekdayOfFirst(year, month);
  const today = todaySP();

  const cells: (Ymd | null)[] = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(formatYmd(year, month, d));

  return (
    <div className="mt-2 rounded-xl border border-border bg-surface p-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label="Mês anterior"
          onClick={() => setViewYm(addMonths(viewYm, -1))}
          className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-semibold capitalize text-ink">{formatMonthLabel(viewYm)}</span>
        <button
          type="button"
          aria-label="Próximo mês"
          onClick={() => setViewYm(addMonths(viewYm, 1))}
          className="grid size-8 place-items-center rounded-full text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w, i) => (
          <span key={i} className="grid h-7 place-items-center text-[11px] font-medium text-muted">
            {w}
          </span>
        ))}
        {cells.map((ymd, i) =>
          ymd === null ? (
            <span key={`e${i}`} />
          ) : (
            <button
              key={ymd}
              type="button"
              onClick={() => onSelect(ymd)}
              className={cn(
                'grid h-9 place-items-center rounded-lg text-sm tabular-nums transition-colors',
                ymd === value
                  ? 'bg-primary font-semibold text-white'
                  : 'text-ink hover:bg-canvas',
                ymd === today && ymd !== value && 'ring-1 ring-inset ring-primary/40',
              )}
            >
              {parseYmd(ymd).day}
            </button>
          ),
        )}
      </div>
    </div>
  );
}

interface Chip {
  label: string;
  date: Ymd;
}

interface DateFieldProps {
  value: Ymd;
  onChange: (d: Ymd) => void;
  /** Mês sendo visualizado no app (para chips contextuais, hint e abertura do calendário). */
  viewedMonth: Ym;
  accent: TxAccent;
}

/** Campo de data ciente do mês visualizado: chips rápidos + calendário + aviso de mês. */
export function DateField({ value, onChange, viewedMonth, accent }: DateFieldProps) {
  const [calOpen, setCalOpen] = useState(false);

  const today = todaySP();
  const chips: Chip[] = [
    { label: 'Hoje', date: today },
    { label: 'Ontem', date: prevDay(today) },
  ];

  const isCurrent = viewedMonth === currentMonthSP();
  if (!isCurrent) {
    const { year: vy, month: vm } = parseYm(viewedMonth);
    chips.push({ label: `1º de ${monthName(viewedMonth)}`, date: formatYmd(vy, vm, 1) });
    chips.push({
      label: `último dia de ${monthName(viewedMonth)}`,
      date: formatYmd(vy, vm, daysInMonth(vy, vm)),
    });
  }

  const outOfMonth = monthOf(value) !== viewedMonth;
  const moveToViewedMonth = () => {
    const { year: vy, month: vm } = parseYm(viewedMonth);
    onChange(formatYmd(vy, vm, effDay(vy, vm, parseYmd(value).day)));
  };

  const pick = (d: Ymd) => {
    onChange(d);
    setCalOpen(false);
  };

  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-[13px] text-muted">Data</label>
        <span className="text-sm font-semibold capitalize text-ink">{formatDatePretty(value)}</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => {
          const active = chip.date === value;
          return (
            <button
              key={chip.label}
              type="button"
              onClick={() => onChange(chip.date)}
              className={cn(
                'rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
                active
                  ? ACTIVE_CHIP[accent]
                  : 'border-border text-muted hover:border-muted hover:text-ink',
              )}
            >
              {chip.label}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setCalOpen((v) => !v)}
          className={cn(
            'flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
            calOpen
              ? 'border-primary bg-primary-weak text-primary'
              : 'border-border text-muted hover:border-muted hover:text-ink',
          )}
        >
          <CalendarDays className="size-3.5" />
          Outros…
        </button>
      </div>

      <AnimatePresence initial={false}>
        {calOpen && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="overflow-hidden"
          >
            <MiniCalendar value={value} initialMonth={viewedMonth} onSelect={pick} />
          </m.div>
        )}
      </AnimatePresence>

      {outOfMonth && (
        <p className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg bg-alert/10 px-3 py-2 text-[13px] text-ink">
          <span>
            Esse lançamento vai para{' '}
            <span className="font-semibold capitalize">{formatMonthLabel(monthOf(value))}</span>, não{' '}
            <span className="font-semibold capitalize">{formatMonthLabel(viewedMonth)}</span>.
          </span>
          <button
            type="button"
            onClick={moveToViewedMonth}
            className="font-semibold text-primary underline-offset-2 hover:underline"
          >
            Trocar?
          </button>
        </p>
      )}
    </div>
  );
}
